import {
  Component,
  Suspense,
  lazy,
  useState,
  type ComponentType,
  type LazyExoticComponent,
  type ReactNode,
} from 'react';

export type FAQDemoKind = 'editor' | 'reader';
const demoLoaders: Record<FAQDemoKind, () => Promise<{ default: ComponentType }>> = {
  editor: () => import('./FAQEditorDemo'),
  reader: () => import('./FAQReaderDemo'),
};
const demoComponents = new Map<FAQDemoKind, LazyExoticComponent<ComponentType>>();

function getDemo(kind: FAQDemoKind) {
  let Demo = demoComponents.get(kind);
  if (!Demo) {
    Demo = lazy(demoLoaders[kind]);
    demoComponents.set(kind, Demo);
  }
  return Demo;
}

class DemoBoundary extends Component<
  { children: ReactNode; onRetry: () => void },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="newsletter-faq-demo-placeholder" role="alert">
        <p>Preview unavailable.</p>
        <button
          type="button"
          className="newsletter-faq-example-toggle"
          onClick={this.props.onRetry}
        >
          Try again
        </button>
      </div>
    );
  }
}

export default function FAQDemoStage({ kind }: { kind: FAQDemoKind }) {
  const [attempt, setAttempt] = useState(0);
  const Demo = getDemo(kind);
  return (
    <DemoBoundary
      key={`${kind}-${attempt}`}
      onRetry={() => {
        demoComponents.delete(kind);
        setAttempt((count) => count + 1);
      }}
    >
      <Suspense
        fallback={
          <div className="newsletter-faq-demo-placeholder" role="status">
            Loading preview…
          </div>
        }
      >
        <Demo />
      </Suspense>
    </DemoBoundary>
  );
}
